/**
 * 班长日报模块 API 工厂函数
 *
 * @module api/daily-report/factory
 * @description 班长日报（班组长班次填报、管理查看与统计分析）的 API 工厂函数
 *
 * @backend backend/app/Modules/Production/Controllers/DailyReportController.php
 * @routes /api/v1/production/daily-reports
 *
 * @author YSFL Team
 * @created 2026-09-19
 * @last-updated 2026-09-21
 */

// ===== 导入类型定义 =====
import type {
  CreateDailyReportForm,
  DailyReport,
  DailyReportDuplicateCheck,
  DailyReportNotificationSwitch,
  DailyReportPreviousHandover,
  DailyReportQueryParams,
  DailyReportStatistics,
  DailyReportStatisticsQueryParams,
  DailyReportTodayOverview,
  UpdateDailyReportForm,
} from '../../types/production/daily-report';

import type { PaginationApiResponse } from '../../types/api';
import type { IRequestClient } from '../core/client';

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes';

/**
 * 创建 DailyReport API 实例
 *
 * @param client HTTP 请求客户端
 * @returns DailyReport API 实例
 */
export function createDailyReportApi(client: IRequestClient) {
  return {
    /**
     * 获取日报列表（分页）
     *
     * 可见范围：无 view-all 权限时按「本人所属部门及其下级部门」收敛，
     * 无部门归属退化为仅本人（与详情/统计同一套规则）
     */
    getList(params?: DailyReportQueryParams) {
      return client.get<PaginationApiResponse<DailyReport>>(API_ROUTES.production.dailyReports, { params });
    },

    /**
     * 获取日报详情
     */
    getDetail(id: number) {
      return client.get<DailyReport>(API_ROUTES.production.productionDailyReports(id));
    },

    /**
     * 创建日报（管理人员可通过 reporter_user_id 代填）
     */
    create(data: CreateDailyReportForm) {
      return client.post<DailyReport>(API_ROUTES.production.dailyReports, data);
    },

    /**
     * 修改日报
     *
     * 管理人员可改任意；本人填写的日报当天内可自行修改，跨天且已提交则锁定
     */
    update(id: number, data: UpdateDailyReportForm) {
      return client.put<DailyReport>(API_ROUTES.production.productionDailyReports(id), data);
    },

    /**
     * 删除日报
     */
    delete(id: number) {
      return client.delete<void>(API_ROUTES.production.productionDailyReports(id));
    },

    /**
     * 获取日报统计（汇总 / 按日期或周期 / 按部门 / 按班次）
     *
     * 缺省返回近 30 天数据，跨度上限 90 天；granularity 支持 day/week/month
     */
    getStatistics(params?: DailyReportStatisticsQueryParams) {
      return client.get<DailyReportStatistics>(API_ROUTES.production.dailyReportsStatistics, { params });
    },

    /**
     * 获取同班组上一班次交接事项（填报页展示，只读）
     */
    getPreviousHandover(params: { report_date: string; shift: string; team_id: number }) {
      return client.get<DailyReportPreviousHandover | null>(
        API_ROUTES.production.dailyReportsPreviousHandover,
        { params },
      );
    },

    /**
     * 重复填报软提示（同班组同日同班次已有他人提交，不阻断）
     *
     * 返回内容与详情同一套可见范围收敛：范围外只返回 duplicated=false，不泄露填报人
     */
    duplicateCheck(params: { report_date: string; shift: string; team_id: number }) {
      return client.get<DailyReportDuplicateCheck>(
        API_ROUTES.production.dailyReportsDuplicateCheck,
        { params },
      );
    },

    /**
     * 确认接收交接事项（可见范围内持更新权限者可确认，允许填报人自收）
     */
    confirmHandover(id: number) {
      return client.put<DailyReport>(API_ROUTES.production.dailyReportsConfirmHandover(id));
    },

    /**
     * 今日日报汇总（管理者视角：应交/已交/未交 + 按车间分组的班组提交状态）
     *
     * 可见范围与列表一致：view-all 全部，其余本部门树班组 + 本人日报
     */
    getTodaySummary() {
      return client.get<DailyReportTodayOverview>(API_ROUTES.production.dailyReportsTodaySummary);
    },

    /**
     * 查询日报推送开关（需 production:daily-report:notification-switch 权限）
     */
    getNotificationSwitch() {
      return client.get<DailyReportNotificationSwitch>(API_ROUTES.production.dailyReportsNotificationSwitch);
    },

    /**
     * 切换日报推送开关（需 production:daily-report:notification-switch 权限）
     */
    updateNotificationSwitch(enabled: boolean) {
      return client.put<DailyReportNotificationSwitch>(API_ROUTES.production.dailyReportsNotificationSwitch, {
        enabled,
      });
    },
  };
}

/**
 * DailyReport API 类型
 */
export type DailyReportApi = ReturnType<typeof createDailyReportApi>;

/**
 * 默认导出
 */
export default createDailyReportApi;
